import * as Mutations from './mutations';
import router from '@/router';
import * as Routes from '@/router/routes';
import { clearAllSelected } from '@/store/modules/page/reset';
import { PaginationParams } from '@/common/utils/pagination-params';
import { CustomFormsService } from '@/common/services/settings/custom-forms.service';

export const CustomFormsModule = {
    state: {
        updateFormButton: false,
        customForms: null,
        draggable: true,
        customForm: {
            enabled: false,
            required: false,
            askType: 'ASK_ONCE',
            familyType: 'EVERY_CHILD',
            formName: '',
            formFields: [
                {
                    sortedId: 0,
                    required: false,
                    question: '',
                    fieldType: 'SHORT_QUESTION',
                    formOption: [
                        {
                            sortedId: 0,
                            option: "",
                        }
                    ]
                }
            ]
        },
        defaultCustomForm: {
            enabled: false,
            required: false,
            askType: 'ASK_ONCE',
            familyType: 'EVERY_CHILD',
            formName: '',
            formFields: [
                {
                    sortedId: 0,
                    required: false,
                    question: '',
                    fieldType: 'SHORT_QUESTION',
                    formOption: [
                        {
                            sortedId: 0,
                            option: "",
                        }
                    ]
                }
            ]
        },
    },

    mutations: {
        [Mutations.SET_CUSTOM_FORMS] (state, customForms) {
            state.customForms = customForms;
        },

        [Mutations.SET_CUSTOM_FORM] (state, customForm) {
            state.customForm = customForm;
        },

        [Mutations.UPDATE_CUSTOM_FORM_BUTTON] (state, payload) {
            state.updateFormButton = payload;
        },

        [Mutations.ADD_NEW_FORM_OPTION_IN_FORM_FIELD] (state, {formFieldId, existFormFieldId, ...option}) {
            state.customForm.formFields[
                existFormFieldId ? (
                    state.customForm.formFields.indexOf(state.customForm.formFields.find(el => el.id === formFieldId))
                ) : state.customForm.formFields.indexOf(state.customForm.formFields.find(el => el.sortedId === formFieldId))
            ].formOption.push(option);
        },

        [Mutations.REMOVE_FORM_OPTION_IN_FORM_FIELD] (state, {el, elementId, existFormFieldId}) {
            const indx = existFormFieldId ? (
                state.customForm.formFields.indexOf(state.customForm.formFields.find(el => el.id === elementId))
            ) : state.customForm.formFields.indexOf(state.customForm.formFields.find(el => el.sortedId === elementId));
            state.customForm.formFields[indx].formOption.splice(
                state.customForm.formFields[indx].formOption.indexOf(el), 
                1
            );
        },

        [Mutations.ADD_NEW_FORM_FIELD_IN_CUSTOM_FORM] (state, formField) {
            state.customForm.formFields.push(formField);
        },

        [Mutations.DUPLICATE_FORM_FIELD_IN_CUSTOM_FORM] (state, formField) {
            state.customForm.formFields.push(formField);
        },

        [Mutations.REMOVE_FORM_FIELD_IN_CUSTOM_FORM] (state, payload) {
            state.customForm.formFields.splice(
                state.customForm.formFields.indexOf(payload), 
                1
            );
        },

        [Mutations.PERIODICITY_SETTINGS_IN_CUSTOM_FORM] (state, payload) {
            state.customForm.askType = payload;
        },

        [Mutations.FAMILY_TYPE_SETTINGS_IN_CUSTOM_FORM] (state, payload) {
            state.customForm.familyType = payload;
        },

        [Mutations.RESET_CUSTOM_FORM] (state) {
            state.customForm = null;
            state.customForm = {...state.defaultCustomForm};
            state.customForm.formFields.forEach(el => el.question = '');
            state.customForm.formFields.splice(0, state.customForm.formFields.length);
            state.customForm.formFields.push({
                sortedId: 0,
                required: false,
                question: '',
                fieldType: 'SHORT_QUESTION',
                formOption: [
                    {
                        sortedId: 0,
                        option: "",
                    }
                ]
            });
        },

        [Mutations.REPLACE_FORM_FIELD_IN_CUSTOM_FORM] (state, payload) {
            state.customForm.formFields.splice(
                payload.to,
                0,
                state.customForm.formFields.splice(payload.from, 1)[0]
            );
        },

        [Mutations.TOGGLE_DRAGGABLE_STATE] (state, payload) {
            state.draggable = payload;
        },
    },

    actions: {
        async getCustomForms(context) {
            const { data: { content, pageable, totalPages, totalElements, numberOfElements } } =
                await CustomFormsService.getCustomForms(context.getters.schoolId,
                    PaginationParams.createParams({
                        size: context.getters.pageSize,
                        page: context.getters.pageNumber,
                        sort: context.getters.sortBy,
                        direction: context.getters.direction,
                        query: context.getters.searchQuery,
                        filters: {
                            ...context.getters.filters
                        }
                    }));
            context.commit(Mutations.SET_CUSTOM_FORMS, content);
            context.dispatch('configPagination', {
                ...pageable,
                numberOfElements,
                totalElements,
                totalPages,
                callback: () => {
                    return context.dispatch('getCustomForms');
                }
            });
            context.dispatch('resetSelectedItems');
        },

        async getAllCustomForms(context) {
            const { data } = await CustomFormsService.getAllCustomForms(context.getters.schoolId)
            context.commit(Mutations.SET_CUSTOM_FORMS, data.content);
        },

        async deleteCustomForm(context, formId) {
            await context.dispatch('deleteCustomForms', [ formId ]);
        },

        async deleteMultipleCustomForms(context) {
            await context.dispatch('deleteCustomForms', context.getters.selected);
            context.dispatch('resetSelectedItems');
        },

        async deleteCustomForms(context, formIds) {
            await CustomFormsService.deleteCustomForms(context.getters.schoolId, formIds);
            if (formIds.length === context.getters.customForms.length) {
                context.dispatch('openPreviousPage');
            }
            clearAllSelected();
            await context.dispatch('getCustomForms');
        },

        async createCustomForm(context) {
            try {
                const form = JSON.stringify(context.getters.customForm);
                await CustomFormsService.createCustomForm(context.getters.schoolId, form);
                context.dispatch('resetCustomForm');
                router.push({ name: Routes.CUSTOM_FORMS_PAGE });
                context.dispatch('createSuccessNotification', 'You have successfully created a form');
            } catch (e) {
                throw e;
            }
        },

        async getCustomFormById(context, id) {
            try {
                const form = await CustomFormsService.getCustomFormById(context.getters.schoolId, id);
                context.dispatch('setCustomForm', form.data);
                context.commit(Mutations.UPDATE_CUSTOM_FORM_BUTTON, true);
            } catch (e) {
                throw e;
            }
        },

        async updateCustomForm(context) {
            try {
                const form = JSON.stringify(context.getters.customForm);
                await CustomFormsService.updateCustomForm(context.getters.schoolId, form);
                context.dispatch('resetCustomForm');
                router.push({ name: Routes.CUSTOM_FORMS_PAGE });
                context.dispatch('createSuccessNotification', 'You have successfully updated a form');
            } catch (e) {
                throw e;
            }
        },

        async duplicateCustomForm(context, payload) {
            try {
                const form = await CustomFormsService.getCustomFormById(context.getters.schoolId, payload);
                const {id, createdAt, updatedAt, ...restForm} = form.data;
                restForm.formFields.forEach(el => {
                    delete el.id;
                    delete el.formId;
                    el.formOption.forEach(element => {
                        delete element.fieldId;
                        delete element.id;
                    })
                })
                await CustomFormsService.createCustomForm(context.getters.schoolId, JSON.stringify(restForm));
                await context.dispatch('getCustomForms');
                context.dispatch('createSuccessNotification', 'You have successfully copied a form');
            } catch (e) {
                throw e;
            }
        },

        addNewFormOptionInFormField(context, payload) {
            context.commit(Mutations.ADD_NEW_FORM_OPTION_IN_FORM_FIELD, payload);
        },

        removeFormOptionInFormField(context, payload) {
            context.commit(Mutations.REMOVE_FORM_OPTION_IN_FORM_FIELD, payload);
        },

        addNewFormFieldInCustomForm(context, payload) {
            context.commit(Mutations.ADD_NEW_FORM_FIELD_IN_CUSTOM_FORM, payload);
        },

        duplicateFormFieldInCustomForm(context, payload) {
            context.commit(Mutations.DUPLICATE_FORM_FIELD_IN_CUSTOM_FORM, payload);
        },

        removeFormFieldInCustomForm(context, payload) {
            context.commit(Mutations.REMOVE_FORM_FIELD_IN_CUSTOM_FORM, payload);
        },

        periodicitySettingsInCustomForm(context, payload) {
            context.commit(Mutations.PERIODICITY_SETTINGS_IN_CUSTOM_FORM, payload);
        },

        familyTypeSettingsInCustomForm(context, payload) {
            context.commit(Mutations.FAMILY_TYPE_SETTINGS_IN_CUSTOM_FORM, payload);
        },

        setCustomForm(context, payload) {
            context.commit(Mutations.SET_CUSTOM_FORM, payload);
        },

        resetCustomForm(context) {
            context.commit(Mutations.RESET_CUSTOM_FORM);
            context.commit(Mutations.UPDATE_CUSTOM_FORM_BUTTON, false);
        },
    },
    getters: {
        customForms: state => state.customForms,

        customForm: state => state.customForm,

        draggable: state => state.draggable,

        defaultCustomForm: state => state.defaultCustomForm,

        updateFormButton: state => state.updateFormButton,
    }
};


// WEBPACK FOOTER //
// ./src/store/modules/custom-forms/custom-forms.module.js